export interface AdminUserProfile {
  id: string;
  username: string;
  email: string;
  role: string;
  status: string;
  scoped_agent_id?: string | null;
}

export interface AdminAuthSession {
  accessToken: string;
  expiresIn: number;
  user: AdminUserProfile;
}

export interface LoginAdminInput {
  login: string;
  password: string;
}

interface AdminAuthSessionResponse {
  access_token: string;
  expires_in: number;
  user: AdminUserProfile;
}

async function readErrorMessage(response: Response): Promise<string> {
  const fallback = response.statusText || 'Request failed.';
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    try {
      const payload = (await response.json()) as { detail?: unknown };
      if (typeof payload.detail === 'string' && payload.detail.trim()) {
        return payload.detail;
      }
      if (payload.detail != null) {
        return JSON.stringify(payload.detail);
      }
      return JSON.stringify(payload);
    } catch {
      return fallback;
    }
  }

  const text = await response.text().catch(() => fallback);
  return text || fallback;
}

function normalizeSession(payload: AdminAuthSessionResponse): AdminAuthSession {
  return {
    accessToken: payload.access_token,
    expiresIn: payload.expires_in,
    user: payload.user,
  };
}

async function requestAdminSession(
  input: RequestInfo | URL,
  init: RequestInit,
  actionLabel: string,
): Promise<AdminAuthSession> {
  const response = await fetch(input, init);

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(`${actionLabel} failed (${response.status}): ${message}`);
  }

  const payload = (await response.json()) as AdminAuthSessionResponse;
  return normalizeSession(payload);
}

export async function loginAdmin(input: LoginAdminInput): Promise<AdminAuthSession> {
  return requestAdminSession(
    '/api/v1/admin/auth/login',
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
    'Login',
  );
}

export async function refreshAdmin(): Promise<AdminAuthSession> {
  return requestAdminSession(
    '/api/v1/admin/auth/refresh',
    {
      method: 'POST',
      credentials: 'include',
    },
    'Refresh',
  );
}

export async function logoutAdmin(): Promise<void> {
  const response = await fetch('/api/v1/admin/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(`Logout failed (${response.status}): ${message}`);
  }
}

export async function getAdminMe(accessToken: string): Promise<AdminUserProfile> {
  const response = await fetch('/api/v1/admin/auth/me', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(`Load admin profile failed (${response.status}): ${message}`);
  }

  return response.json() as Promise<AdminUserProfile>;
}
